import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.ip.management.renewals');
import { OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

type Option = {
    id: string;
    name?: string;
    title?: string;
    case_number?: string;
    right_type?: string;
    application_number?: string;
    registration_number?: string;
};

type RenewalDraft = {
    id: string;
    version: number;
    tenant_id: string;
    managed_client_id: string;
    case_id: string;
    right_id: string;
    schedule_type: 'RENEWAL' | 'ANNUITY';
    cycle_label: string;
    period_start: string;
    period_end: string;
    due_date: string;
    notes: string;
};

@Component({
    selector: 'wiz-page-ip-management-renewals',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.ip.management.renewals/view.scss */
:host {
  display: block;
  min-height: 100%;
}

button,
select,
input,
textarea {
  transition: border-color 150ms ease, background-color 150ms ease, color 150ms ease;
}

@media (prefers-reduced-motion: reduce) {
  button,
  select,
  input,
  textarea {
    transition: none;
  }
  .animate-spin {
    animation: none;
  }
}`],
})
export class PageIpManagementRenewalsComponent implements OnInit {
    public rows: any[] = [];
    public summary: any = {
        total: 0,
        pending: 0,
        due_30: 0,
        overdue: 0,
        completed: 0
    };
    public filters = {
        search: '',
        tenant_id: '',
        schedule_type: '',
        status: ''
    };
    public tenants: Option[] = [];
    public clients: Option[] = [];
    public cases: Option[] = [];
    public rights: Option[] = [];
    public page = 1;
    public pageSize = 20;
    public total = 0;
    public loading = true;
    public saving = false;
    public changingStatusId = '';
    public editorOpen = false;
    public errorMessage = '';
    public editorError = '';
    public successMessage = '';
    public draft: RenewalDraft = this.emptyDraft();

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        await this.service.auth.allow('/access');
        await this.loadRootOptions();
        await this.loadSchedules();
    }

    private responseData(response: any) {
        const payload = response && response.responseJSON ? response.responseJSON : response;
        const status = Number(payload?.code ?? response?.status ?? 500);
        const data = payload?.data;
        const errorData = data?.error || data || {};
        if (!payload || status >= 400) {
            const error: any = new Error(
                payload?.message
                || (typeof data === 'string' ? data : errorData?.message)
                || response?.statusText
                || '요청을 처리하지 못했습니다.'
            );
            error.code = payload?.error_code
                || errorData?.error_code
                || errorData?.code
                || 'REQUEST_FAILED';
            error.status = status;
            throw error;
        }
        return data;
    }

    private captureError(error: any, editor = false) {
        const message = String(error?.message || '요청을 처리하지 못했습니다.');
        if (editor) {
            this.editorError = message;
        } else {
            this.errorMessage = message;
        }
    }

    private emptyDraft(): RenewalDraft {
        const start = new Date();
        const end = new Date(start.getTime());
        end.setDate(end.getDate() + 30);
        return {
            id: '',
            version: 0,
            tenant_id: '',
            managed_client_id: '',
            case_id: '',
            right_id: '',
            schedule_type: 'ANNUITY',
            cycle_label: '',
            period_start: this.inputDate(start),
            period_end: this.inputDate(end),
            due_date: this.inputDate(end),
            notes: ''
        };
    }

    private inputDate(value: Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return year + '-' + month + '-' + day;
    }

    private async loadRootOptions() {
        try {
            const data: any = this.responseData(await wiz.call('options'));
            this.tenants = data?.tenants || [];
        } catch (error) {
            this.captureError(error);
        }
    }

    public async loadSchedules() {
        this.loading = true;
        this.errorMessage = '';
        await this.service.render();
        try {
            const data: any = this.responseData(await wiz.call('list_schedules', {
                search: this.filters.search.trim(),
                tenant_id: this.filters.tenant_id,
                schedule_type: this.filters.schedule_type,
                status: this.filters.status,
                page: this.page,
                page_size: this.pageSize
            }));
            this.rows = data?.items || [];
            this.summary = data?.summary || this.summary;
            this.total = Number(data?.total || 0);
            const maxPage = this.pageCount;
            if (this.page > maxPage) {
                this.page = maxPage;
                await this.loadSchedules();
                return;
            }
        } catch (error) {
            this.rows = [];
            this.captureError(error);
        } finally {
            this.loading = false;
            await this.service.render();
        }
    }

    public async applyFilters() {
        this.page = 1;
        await this.loadSchedules();
    }

    public async clearFilters() {
        this.filters = {
            search: '',
            tenant_id: '',
            schedule_type: '',
            status: ''
        };
        this.page = 1;
        await this.loadSchedules();
    }

    public get hasFilters() {
        return !!(
            this.filters.search.trim()
            || this.filters.tenant_id
            || this.filters.schedule_type
            || this.filters.status
        );
    }

    public get pageCount() {
        return Math.max(1, Math.ceil(this.total / this.pageSize));
    }

    public get pageNumbers() {
        const start = Math.max(1, Math.min(this.page - 2, this.pageCount - 4));
        const end = Math.min(this.pageCount, start + 4);
        return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    }

    public async goToPage(page: number) {
        if (page < 1 || page > this.pageCount || page === this.page) return;
        this.page = page;
        await this.loadSchedules();
    }

    public async openCreate() {
        this.draft = this.emptyDraft();
        this.clients = [];
        this.cases = [];
        this.rights = [];
        this.editorError = '';
        this.editorOpen = true;
        await this.service.render();
    }

    public async openEdit(item: any) {
        this.draft = {
            id: item.id,
            version: item.version,
            tenant_id: item.tenant_id,
            managed_client_id: item.managed_client_id,
            case_id: item.case_id,
            right_id: item.right_id,
            schedule_type: item.schedule_type,
            cycle_label: item.cycle_label || '',
            period_start: item.period_start,
            period_end: item.period_end,
            due_date: item.due_date,
            notes: item.notes || ''
        };
        this.clients = [];
        this.cases = [];
        this.rights = [];
        this.editorError = '';
        this.editorOpen = true;
        await this.service.render();
        await this.loadEditorOptions();
    }

    private async loadEditorOptions() {
        try {
            const clientData: any = this.responseData(await wiz.call('options', {
                tenant_id: this.draft.tenant_id
            }));
            this.clients = clientData?.clients || [];
            const caseData: any = this.responseData(await wiz.call('options', {
                tenant_id: this.draft.tenant_id,
                managed_client_id: this.draft.managed_client_id
            }));
            this.cases = caseData?.cases || [];
            const rightData: any = this.responseData(await wiz.call('options', {
                tenant_id: this.draft.tenant_id,
                managed_client_id: this.draft.managed_client_id,
                case_id: this.draft.case_id
            }));
            this.rights = rightData?.rights || [];
        } catch (error) {
            this.captureError(error, true);
        }
        await this.service.render();
    }

    public async onTenantChange() {
        this.draft.managed_client_id = '';
        this.draft.case_id = '';
        this.draft.right_id = '';
        this.clients = [];
        this.cases = [];
        this.rights = [];
        this.editorError = '';
        if (!this.draft.tenant_id) {
            await this.service.render();
            return;
        }
        try {
            const data: any = this.responseData(await wiz.call('options', {
                tenant_id: this.draft.tenant_id
            }));
            this.clients = data?.clients || [];
        } catch (error) {
            this.captureError(error, true);
        }
        await this.service.render();
    }

    public async onClientChange() {
        this.draft.case_id = '';
        this.draft.right_id = '';
        this.cases = [];
        this.rights = [];
        this.editorError = '';
        if (!this.draft.managed_client_id) {
            await this.service.render();
            return;
        }
        try {
            const data: any = this.responseData(await wiz.call('options', {
                tenant_id: this.draft.tenant_id,
                managed_client_id: this.draft.managed_client_id
            }));
            this.cases = data?.cases || [];
        } catch (error) {
            this.captureError(error, true);
        }
        await this.service.render();
    }

    public async onCaseChange() {
        this.draft.right_id = '';
        this.rights = [];
        this.editorError = '';
        if (!this.draft.case_id) {
            await this.service.render();
            return;
        }
        try {
            const data: any = this.responseData(await wiz.call('options', {
                tenant_id: this.draft.tenant_id,
                managed_client_id: this.draft.managed_client_id,
                case_id: this.draft.case_id
            }));
            this.rights = data?.rights || [];
        } catch (error) {
            this.captureError(error, true);
        }
        await this.service.render();
    }

    public closeEditor() {
        if (this.saving) return;
        this.editorOpen = false;
        this.editorError = '';
    }

    public async save(event?: Event) {
        event?.preventDefault();
        if (this.saving) return;
        this.editorError = '';
        if (
            !this.draft.tenant_id
            || !this.draft.managed_client_id
            || !this.draft.case_id
            || !this.draft.right_id
            || !this.draft.period_start
            || !this.draft.period_end
            || !this.draft.due_date
        ) {
            this.editorError = '사무소, 기업체, 사건, IP 권리와 관리 기간을 모두 입력해주세요.';
            await this.service.render();
            return;
        }
        if (this.draft.period_start > this.draft.period_end) {
            this.editorError = '관리 시작일은 종료일보다 늦을 수 없습니다.';
            await this.service.render();
            return;
        }
        if (
            this.draft.due_date < this.draft.period_start
            || this.draft.due_date > this.draft.period_end
        ) {
            this.editorError = '납부·처리 기한은 관리 기간 안에 있어야 합니다.';
            await this.service.render();
            return;
        }

        this.saving = true;
        await this.service.render();
        try {
            const saved: any = this.responseData(await wiz.call('save_schedule', {
                data: JSON.stringify(this.draft)
            }));
            this.editorOpen = false;
            this.successMessage = saved.schedule_type === 'RENEWAL'
                ? '갱신 일정을 저장했습니다.'
                : '연차료 일정을 저장했습니다.';
            await this.loadSchedules();
        } catch (error) {
            this.captureError(error, true);
        } finally {
            this.saving = false;
            await this.service.render();
        }
    }

    public async changeStatus(item: any, target: 'PENDING' | 'COMPLETED' | 'CANCELLED') {
        if (this.changingStatusId) return;
        const action = target === 'COMPLETED'
            ? '처리 완료'
            : (target === 'CANCELLED' ? '일정 취소' : '처리 대기로 복원');
        const confirmed = await this.service.modal.show({
            title: action,
            message: item.company_name + '의 ' + this.typeLabel(item.schedule_type)
                + ' 일정을 ' + action + ' 상태로 변경합니다.',
            action,
            cancel: '닫기',
            status: target === 'CANCELLED' ? 'warning' : 'success',
            actionBtn: target === 'CANCELLED' ? 'warning' : 'success'
        });
        if (!confirmed) return;

        this.changingStatusId = item.id;
        this.errorMessage = '';
        this.successMessage = '';
        await this.service.render();
        try {
            this.responseData(await wiz.call('set_status', {
                id: item.id,
                version: item.version,
                status: target
            }));
            this.successMessage = action + ' 상태로 변경했습니다.';
            await this.loadSchedules();
        } catch (error) {
            this.captureError(error);
        } finally {
            this.changingStatusId = '';
            await this.service.render();
        }
    }

    public typeLabel(value: string) {
        return value === 'RENEWAL' ? '권리 갱신' : '연차료';
    }

    public rightTypeLabel(value: string) {
        const labels: any = {
            PATENT: '특허',
            UTILITY_MODEL: '실용신안',
            TRADEMARK: '상표',
            DESIGN: '디자인',
            OTHER: '기타'
        };
        return labels[value] || value || '-';
    }

    public statusLabel(value: string) {
        const labels: any = {
            PENDING: '처리 대기',
            OVERDUE: '기한 경과',
            COMPLETED: '처리 완료',
            CANCELLED: '취소'
        };
        return labels[value] || value;
    }

    public statusClass(value: string) {
        if (value === 'COMPLETED') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
        if (value === 'OVERDUE') return 'border-rose-200 bg-rose-50 text-rose-700';
        if (value === 'CANCELLED') return 'border-slate-200 bg-slate-100 text-slate-500';
        return 'border-amber-200 bg-amber-50 text-amber-700';
    }

    public typeClass(value: string) {
        return value === 'RENEWAL'
            ? 'bg-cyan-50 text-cyan-700 ring-cyan-200'
            : 'bg-violet-50 text-violet-700 ring-violet-200';
    }

    public formatDate(value: string) {
        if (!value) return '-';
        return value.replace(/-/g, '.');
    }

    public dDay(value: string) {
        if (!value) return '';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(value + 'T00:00:00');
        const difference = Math.round((target.getTime() - today.getTime()) / 86400000);
        if (difference === 0) return 'D-Day';
        return difference > 0 ? 'D-' + difference : 'D+' + Math.abs(difference);
    }

    public rightNumber(item: any) {
        return item.registration_number || item.application_number || '-';
    }

    public optionRightLabel(item: any) {
        const number = item.registration_number || item.application_number || '번호 없음';
        return item.title + ' · ' + number;
    }
}

export default PageIpManagementRenewalsComponent;